import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import Analytics from 'packages/analytics';
import { logger } from 'packages/logger';
import { loadScript } from 'tradera/utils/script';
import { localStore } from 'tradera/utils/storage';
import { STORAGE_KEYS } from 'tradera/constants/storage';
import {
    useCurrentLanguage,
    useTranslator,
    useUrlLocalizer
} from 'tradera/lang/translate';
import { getLoginLocaleFromLanguage } from './get-login-locale-from-language';
import { AppleButton } from 'tradera/components/social-buttons/social-buttons';

const AppleLoginButton = (props) => {
    const {
        appleClientId,
        className,
        compact,
        disabled,
        isModal,
        onError,
        tabIndex
    } = props;
    const [isLoading, setIsLoading] = useState(false);
    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();
    const { language } = useCurrentLanguage();
    const locale = getLoginLocaleFromLanguage(language);

    // Pre-loading & init needed on load to prevent popup blocking in Safari
    useEffect(() => {
        (async () => {
            await loadScript(
                `https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/${
                    locale || 'sv_SE'
                }/appleid.auth.js`
            );
            const AppleID = window.AppleID;

            if (!AppleID) {
                logger('Apple SDK could not be loaded by Apple login button');
                return;
            }

            AppleID.auth.init({
                clientId: appleClientId,
                scope: 'name email',
                redirectURI: location.origin + '/login',
                state: '',
                usePopup: true
            });
        })();
    }, [appleClientId, locale]);

    const onClick = async () => {
        if (disabled) {
            return;
        }

        setIsLoading(true);
        onError(null);

        if (isModal) {
            Analytics.trackEvent({
                category: 'Top navigation',
                action: 'Login menu',
                label: 'Button - Apple login'
            });
        }

        try {
            const appleSignInResponse = await window.AppleID.auth.signIn();
            // User data is only provided on first login, so save it for eventual user registration
            // https://developer.apple.com/documentation/sign_in_with_apple/sign_in_with_apple_js/configuring_your_webpage_for_sign_in_with_apple
            if (appleSignInResponse?.user) {
                localStore.setItem(
                    STORAGE_KEYS.APPLE_ID_USER,
                    appleSignInResponse.user
                );
            }
            const verifyResponse = await axios.post('/login/apple/verify', {
                AccessToken: appleSignInResponse.authorization.id_token,
                UserId: 'NOT_USED'
            });
            window.parent.location = verifyResponse.data.returnUrl;
        } catch (error) {
            const message =
                error?.response?.data?.error || error?.message || error?.error;
            switch (message) {
                case 'popup_closed_by_user':
                    break;
                case 'MemberAuth_IncorrectUserOrPassword':
                    window.parent.location = toLocalizedUrl(
                        '/login?connect=Apple'
                    );
                    break;
                default:
                    onError(error);
                    logger(message);
                    break;
            }
        }

        setIsLoading(false);
    };

    return (
        <AppleButton
            onClick={onClick}
            disabled={disabled}
            tabIndex={tabIndex}
            round={compact}
            loading={isLoading}
            className={className}>
            {!compact && t('login_withProvider', { provider: 'Apple' })}
        </AppleButton>
    );
};

AppleLoginButton.propTypes = {
    appleClientId: PropTypes.string,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    isModal: PropTypes.bool,
    tabIndex: PropTypes.number
};

AppleLoginButton.defaultProps = {
    className: '',
    tabIndex: 0
};

export default AppleLoginButton;
